/**
 * Helper Content Repository
 *
 * Centralized explanations for the LRB neuro-informed approach.
 * Used by HelperIcon components throughout the application.
 */

export const helperContent = {
  // =========================================================================
  // General LRB Approach
  // =========================================================================
  lrbApproach: {
    title: "What is the LRB Neuro-Informed Approach?",
    content: `L.R. Brain Solutions uses research-backed, brain-friendly teaching strategies designed to maximize learning retention and student engagement.

Our AI assistants are trained to:
- Apply neuro-informed techniques (spaced retrieval, chunking, interleaving)
- Foster growth mindset and resilience
- Personalize based on learning style
- Make connections to student interests
- Celebrate progress and breakthroughs

Every AI suggestion follows our educational framework to ensure consistent, high-quality tutoring support.`
  },

  // =========================================================================
  // Session Planner
  // =========================================================================
  sessionPlanning: {
    title: "How AI-Assisted Planning Works",
    content: `Our Session Planner uses your student's history to create personalized lesson plans.

How it works:
- Analyzes all past session transcripts and summaries
- Identifies what teaching methods worked best
- Recognizes current challenges and learning patterns
- Suggests activities grounded in learning science

Each suggestion includes the neuro-technique being applied (like Spaced Retrieval or Chunking) so you understand the "why" behind the recommendation.`
  },

  // =========================================================================
  // Resource Aggregation
  // =========================================================================
  resourceAggregation: {
    title: "Why We Aggregate Student Data",
    content: `The brain learns by building connections. Our platform aggregates student resources to help both tutors and AI build on prior knowledge.

What gets aggregated:
- Session transcripts (what was covered)
- Summaries (key outcomes and challenges)
- Uploaded materials (textbooks, worksheets)
- Student profile (learning style, goals)

This reduces your cognitive load as a tutor - you don't have to remember everything. The AI has the full picture to make informed suggestions.`
  },

  // =========================================================================
  // Automated Workflow
  // =========================================================================
  workflowAutomation: {
    title: "The Automated Workflow",
    content: `Our platform automates the journey from tutoring session to actionable insights.

The flow:
1. Google Meet Session (you teach)
2. Sync Transcripts (one click)
3. Auto-link to student profiles
4. Auto-generate AI summary
5. Resources appear in Session Planner
6. AI plans with full context

This automation means you spend less time on admin and more time on what matters - helping students learn.`
  },

  // =========================================================================
  // Tutor Matching
  // =========================================================================
  matchingRationale: {
    title: "How Tutor Matching Works",
    content: `Our AI matching goes beyond subject expertise to find the best tutor-student fit.

Matching considers:
- Subject knowledge (must have competency)
- Learning style compatibility (reduces friction)
- Communication style alignment (emotional safety)
- Shared interests (builds rapport and motivation)
- Special needs accommodation (when applicable)

Great tutoring is about connection. We use the "Social Brain" principle - learning is enhanced when students feel understood and connected to their tutor.`
  },

  // =========================================================================
  // Whiteboard AI
  // =========================================================================
  whiteboardAI: {
    title: "How AI Assists Tutoring",
    content: `The Whiteboard AI is your intelligent assistant during tutoring sessions.

What it can do:
- Add notes with contextual awareness
- Create math equations (LaTeX rendered)
- Plot graphs and visualizations
- Answer questions about the session

The AI uses "Peripheral Perception" - visual representations help information bypass working memory limits. When you graph an equation or add a diagram, you're making abstract concepts concrete.`
  },

  // =========================================================================
  // Neuro-Informed Badge
  // =========================================================================
  neuroInformedBadge: {
    title: "What Does This Badge Mean?",
    content: `When you see this badge, the AI is applying a specific brain-friendly teaching technique.

Common techniques:
- Spaced Retrieval: Reviewing previous topics at intervals
- Chunking: Breaking complex ideas into parts
- Interleaving: Mixing different topics
- Growth Mindset: Reframing struggle as growth
- Social Brain: Collaborative, inclusive approach

Research shows these techniques lead to better long-term retention than traditional methods.`
  },

  // =========================================================================
  // Student Roster
  // =========================================================================
  studentRoster: {
    title: "Your Assigned Students",
    content: `The Student Roster shows students actively assigned to you.

Assignment workflow:
1. Our AI suggests matches (based on compatibility)
2. Admin reviews and approves assignments
3. Approved students appear in your roster
4. Click any student to start planning sessions

This ensures every student-tutor pairing has been thoughtfully considered for the best learning outcomes.`
  },

  matchApproval: {
    title: "Assignment Approval Process",
    content: `AI-suggested matches require admin review before activation.

Why we do this:
- AI matches are starting points, not final decisions
- Human judgment ensures relationship quality
- Allows consideration of factors AI can't see (tutor availability, family preferences)

Approval options:
- Approve: Student appears in tutor's roster, sessions can begin
- Reject: Match is cancelled, student returns to matching pool

This two-step process ensures every pairing receives proper oversight.`
  },

  // =========================================================================
  // Emotional Climate
  // =========================================================================
  emotionalClimate: {
    title: "Why Emotional Safety Matters",
    content: `The brain's emotional center (amygdala) directly impacts learning ability.

When a student feels anxious or threatened:
- Working memory capacity decreases
- Ability to process new information drops
- "Fight or flight" response can block learning

Our AI is trained to:
- Treat errors as learning opportunities
- Use encouraging, growth-focused language
- Never use shaming or punitive tones
- Celebrate effort, not just results

A safe learning environment is the foundation for effective tutoring.`
  },

  // =========================================================================
  // Session Phase Explanations (for SessionPhaseIndicator)
  // =========================================================================
  sessionPhases: {
    title: "Why Session Phases?",
    content: `Sessions are structured in three phases, each targeting different brain systems:

1. **Opening** - Activates the Alerting Network
   - Norepinephrine release prepares the brain for learning
   - Hooks and review questions prime existing neural pathways

2. **Core** - Enables patterning and deep learning
   - Visual representations bypass working memory limits
   - Active thinking creates stronger neural connections

3. **Closing** - Builds metacognitive reflection
   - Self-assessment strengthens what was just learned
   - Previewing next steps supports spaced retrieval

This structure is based on neuropedagogy research showing how the brain optimally processes and retains information.`
  },

  openingPhase: {
    title: "Opening Phase: Activating the Alerting Network",
    content: `The Opening phase triggers the brain's Alerting Network, preparing it to receive new information.

Why this matters:
- Norepinephrine release makes the brain more receptive
- A compelling hook captures attention and signals importance
- Reviewing prior knowledge activates existing neural pathways

Best practices:
- Start with something intriguing (puzzle, question, image)
- Connect today's topic to previous sessions
- Prime the student for what's coming`
  },

  corePhase: {
    title: "Core Phase: Patterning & Active Thinking",
    content: `The Core phase is where deep learning happens through visual patterning and active engagement.

Why this matters:
- The brain resists information it cannot categorize
- Visual representations bypass working memory limits
- Active thinking > passive watching for retention

Best practices:
- Use graphs, diagrams, and concept maps
- Encourage the student to draw their understanding
- Switch topics every 15-20 minutes (interleaving)`
  },

  closingPhase: {
    title: "Closing Phase: Metacognitive Reflection",
    content: `The Closing phase builds metacognition - the ability to think about one's own thinking.

Why this matters:
- Reflection strengthens neural pathways just created
- Self-assessment builds independent learning skills
- Previewing next session supports spaced retrieval

Best practices:
- Ask: "What strategy helped most today?"
- Ask: "What's one thing you'll remember?"
- Create a visual summary together`
  }
};

// =========================================================================
// Neuro-Technique Definitions (for badges)
// =========================================================================
export const neuroTechniques = {
  'Spaced Retrieval': {
    name: 'Spaced Retrieval',
    description: 'Reviewing previous topics at optimal intervals for long-term retention',
    icon: null
  },
  'Chunking': {
    name: 'Chunking',
    description: 'Breaking complex concepts into smaller, manageable parts',
    icon: null
  },
  'Interleaving': {
    name: 'Interleaving',
    description: 'Mixing different topics for better long-term retention',
    icon: null
  },
  'Growth Mindset': {
    name: 'Growth Mindset',
    description: 'Reframing struggle as growth and celebrating effort',
    icon: null
  },
  'Social Brain': {
    name: 'Social Brain',
    description: 'Collaborative activities leveraging social learning',
    icon: null
  },
  'Reward Uncertainty': {
    name: 'Reward Uncertainty',
    description: 'Variable rewards for increased engagement',
    icon: null
  },
  'Peripheral Perception': {
    name: 'Peripheral Perception',
    description: 'Using visual aids, graphs, and illustrations',
    icon: null
  },
  'Connection Building': {
    name: 'Connection Building',
    description: 'Linking new concepts to prior knowledge',
    icon: null
  },
  'Cognitive Load Management': {
    name: 'Cognitive Load Management',
    description: 'Keeping information concise to avoid overload',
    icon: null
  },
  'Alerting Cue': {
    name: 'Alerting Cue',
    description: 'Triggering attention with importance signals',
    icon: null
  },
  'Safe Learning Space': {
    name: 'Safe Learning Space',
    description: 'Maintaining emotional safety so the brain can learn without stress',
    icon: null
  },
  'Metacognition': {
    name: 'Metacognition',
    description: 'Building self-awareness about thinking and learning strategies',
    icon: null
  }
};

// Helper function to get content by key
export const getHelperContent = (key) => {
  return helperContent[key] || null;
};

// Helper function to get technique info
export const getTechniqueInfo = (techniqueName) => {
  return neuroTechniques[techniqueName] || null;
};

export default helperContent;
